import {ERROR_MSG, ERRORS_BY_STATUS} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import type {
  APIResult,
  CopilotSpaceCategory,
  CopilotSpaceId,
  CopilotSpacePayload,
  CopilotSpacesCounts,
  FailedAPIResult,
  IndexCopilotSpace,
} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {customCopilotApiPath} from '@github-ui/copilot-chat/utils/custom-copilots-helpers'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import {parseLinkHeader} from './parse-link-header'

const urlPathPrefix = '/github-copilot/chat'

/** Default number of items per page for paginated requests */
export const DEFAULT_PER_PAGE = 30

/** Maximum number of items per page for paginated requests */
export const MAX_PER_PAGE = 100

/** Minimum number of items per page for paginated requests */
export const MIN_PER_PAGE = 1

export const COPILOT_SPACES_QUERY_KEY = ['copilot-chat', 'custom-copilots']
export const COPILOT_SPACES_COUNT_QUERY_KEY = ['copilot-chat', 'custom-copilots', 'count']

/**
 * Returns a query key for fetching spaces by category type.
 * @param type - The category type for filtering spaces
 */
export function copilotSpacesCategoryQueryKey(type: CopilotSpaceCategory) {
  return ['copilot-chat', 'custom-copilots', type]
}

export function copilotSpacesQueryKey(id: CopilotSpaceId | null) {
  if (!id) {
    return ['copilot-chat', 'custom-copilot', null]
  }
  return ['copilot-chat', 'custom-copilot', {id: id.id, owner: id.owner}]
}

export interface VisibilitySettings {
  memberCount: number | null
}

export interface MenuActionsData {
  visibility: string
  shareable: boolean
  editable: boolean
  adminable: boolean
  baseRole: string
  public: boolean
}

/**
 * Pagination cursor information extracted from Link headers.
 * Following RFC 5988 standard for pagination.
 */
export interface PaginationCursors {
  /** URL for the next page (from rel="next" in Link header) */
  nextCursor: string | null
  /** URL for the previous page (from rel="prev" in Link header) */
  prevCursor: string | null
}

/**
 * Result of a paginated fetch operation including spaces and pagination info.
 */
export interface PaginatedCopilotSpacesResult {
  /** Array of Copilot Space objects for this page */
  spaces: IndexCopilotSpace[]
  /** Pagination cursor information from Link headers */
  pagination: PaginationCursors
}

export async function fetchCopilotSpaces(type?: CopilotSpaceCategory): Promise<IndexCopilotSpace[]> {
  const url = type ? `${urlPathPrefix}/custom_copilots?type=${type}` : `${urlPathPrefix}/custom_copilots`
  const res = await verifiedFetchJSON(url)
  if (!res.ok) throw new Error(`Failed to fetch spaces: ${ERRORS_BY_STATUS[res.status] || ERROR_MSG}`)
  const payload = await res.json()
  return payload
}

/**
 * Fetches a page of Copilot Spaces with cursor-based pagination support.
 * Pagination cursors are extracted from the Link header per RFC 5988.
 *
 * @param options - Fetch options
 * @param options.type - Category filter (starred, user_owned, org_owned, shared_with_user)
 * @param options.perPage - Number of items per page (default: 30, max: 100)
 * @param options.cursor - URL cursor for pagination (from previous response's nextCursor or prevCursor)
 * @returns Promise with spaces array and pagination cursor information
 */
export async function fetchPaginatedCopilotSpaces(options: {
  type?: CopilotSpaceCategory
  perPage?: number
  cursor?: string | null
}): Promise<PaginatedCopilotSpacesResult> {
  const {type, perPage = DEFAULT_PER_PAGE, cursor} = options

  let url: string
  if (cursor) {
    // Use the cursor URL but ensure the type parameter is preserved
    // The Link header cursor URL may not include the type parameter
    const cursorUrl = new URL(cursor, window.location.origin)
    if (type && !cursorUrl.searchParams.has('type')) {
      cursorUrl.searchParams.set('type', type)
    }
    url = cursorUrl.pathname + cursorUrl.search
  } else {
    // Build initial request URL
    const params = new URLSearchParams()
    if (type) params.set('type', type)
    params.set('per_page', String(Math.min(Math.max(perPage, MIN_PER_PAGE), MAX_PER_PAGE)))
    const queryString = params.toString()
    url = `${urlPathPrefix}/custom_copilots${queryString ? `?${queryString}` : ''}`
  }

  const res = await verifiedFetchJSON(url)
  if (!res.ok) throw new Error(`Failed to fetch spaces: ${ERRORS_BY_STATUS[res.status] || ERROR_MSG}`)

  const spaces: IndexCopilotSpace[] = await res.json()

  // Extract pagination cursors from Link header
  const linkHeader = res.headers.get('Link')
  const pagination: PaginationCursors = {
    nextCursor: parseLinkHeader(linkHeader, 'next'),
    prevCursor: parseLinkHeader(linkHeader, 'prev'),
  }

  return {spaces, pagination}
}

export async function searchCopilotSpaces(type: CopilotSpaceCategory, query: string): Promise<IndexCopilotSpace[]> {
  const url = `${urlPathPrefix}/custom_copilots/search?type=${type}&q=${encodeURIComponent(query)}`
  const res = await verifiedFetchJSON(url)
  if (!res.ok) throw new Error(`Failed to search spaces: ${ERRORS_BY_STATUS[res.status] || ERROR_MSG}`)
  const payload = await res.json()
  return payload
}

export async function fetchCopilotSpace(
  customCopilotID: CopilotSpaceId | null,
): Promise<APIResult<CopilotSpacePayload>> {
  // This should never run because we're setting
  // the enabled property to false in the useQuery call that uses it
  if (!customCopilotID || !customCopilotID.owner) {
    return {status: 500, ok: false, error: 'unexpected null custom copilot id or missing owner'}
  }
  const {owner, id} = customCopilotID
  const url = `${urlPathPrefix}/custom_copilots/${owner}/${id}`

  const res = await verifiedFetchJSON(url)
  if (!res.ok) return {status: res.status, ok: false, error: ERRORS_BY_STATUS[res.status] || ERROR_MSG}
  const payload = await res.json()
  return {status: res.status, ok: true, payload}
}

export async function deleteCopilotSpace(copilotId: CopilotSpaceId): Promise<APIResult<null>> {
  const res = await verifiedFetchJSON(customCopilotApiPath(copilotId), {method: 'DELETE'})
  if (!res.ok) return res as unknown as FailedAPIResult

  return {status: res.status, ok: true, payload: null}
}

export async function fetchVisibilitySettings(copilotId: CopilotSpaceId): Promise<VisibilitySettings> {
  const res = await verifiedFetchJSON(`${customCopilotApiPath(copilotId)}/settings/visibility`)
  if (!res.ok) throw new Error(`Error fetching visibility settings`)
  try {
    const data = await res.json()
    return {
      memberCount: data.member_count && typeof data.member_count === 'number' ? (data.member_count as number) : null,
    }
  } catch (error) {
    throw new Error(`Error parsing visibility settings: ${error}`)
  }
}

export async function fetchMenuActions(copilotId: CopilotSpaceId): Promise<MenuActionsData> {
  const res = await verifiedFetchJSON(`${customCopilotApiPath(copilotId)}/menu_actions`)
  if (!res.ok) throw new Error(`Error fetching menu actions`)
  try {
    const data = await res.json()
    return {
      visibility: data.visibility ?? 'private',
      shareable: data.shareable ?? false,
      editable: data.editable ?? false,
      adminable: data.adminable ?? false,
      baseRole: data.baseRole ?? 'none',
      public: data.public ?? false,
    }
  } catch (error) {
    throw new Error(`Error parsing menu actions: ${error}`)
  }
}

/**
 * Fetches the count of spaces for each category.
 * @returns The counts for each space category
 */
export async function fetchSpacesCount(): Promise<CopilotSpacesCounts> {
  const res = await verifiedFetchJSON(`${urlPathPrefix}/custom_copilots/count`)
  if (!res.ok) throw new Error(`Failed to fetch spaces count: ${ERRORS_BY_STATUS[res.status] || ERROR_MSG}`)
  return res.json()
}
